import { useState } from 'react';
import { useLoaderData, useNavigate, useRevalidator, useSearchParams } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { Badge, Button, Card, Icon, IconButton, KpiCard } from '@ds/core';
import { DataTable, DocumentCard, FilterBar } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, EmptyState, Modal } from '@ds/feedback';
import { Checkbox, SearchField } from '@ds/forms';
import { Menu, Pagination, Tabs } from '@ds/navigation';
import { ExportMenu } from '@/comuns/ExportMenu';
import { PageHeader } from '@/comuns/PageHeader';
import { dataEHoraPorExtenso } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { TODOS_OS_REGISTROS } from '@/comuns/exportar';
import type {
    AbaDeDocumentos, CargaDeDocumentos, Documento, ModeloDeDocumento, StatusDoDocumento,
} from '@/data/tipos';
import { rotaPorId } from '@/router/rotas';
import { useRotaAtual } from '@/router/useRotaAtual';
import { DetalheDoDocumento, STATUS } from './documentos/DetalheDoDocumento';
import { ModeloModal } from './documentos/ModeloModal';
import { NovoDocumentoModal } from './documentos/NovoDocumentoModal';

const POR_PAGINA = 10;

const ABAS: AbaDeDocumentos[] = ['todos', 'assinaturas', 'modelos'];

const STATUS_FILTRAVEIS: StatusDoDocumento[] = ['assinado', 'aguardando', 'rascunho', 'rejeitado'];

const CATEGORIA_TOM: Record<string, 'brand' | 'info' | 'neutral' | 'success'> = {
    Consentimento: 'brand', Contrato: 'info', Formulário: 'neutral', Orientação: 'success',
};

function abaDaRota(pathname: string): AbaDeDocumentos {
    const ultimo = pathname.split('/').pop() ?? '';
    return (ABAS as string[]).includes(ultimo) ? (ultimo as AbaDeDocumentos) : 'todos';
}

export async function carregarDocumentos({ request }: LoaderFunctionArgs): Promise<CargaDeDocumentos> {
    const url = new URL(request.url);
    return dados().documentos({
        aba: abaDaRota(url.pathname),
        busca: url.searchParams.get('q') ?? undefined,
        tipos: url.searchParams.getAll('tipo'),
        status: url.searchParams.getAll('status') as StatusDoDocumento[],
        favoritos: url.searchParams.get('favoritos') === '1',
        pagina: Number(url.searchParams.get('pagina') ?? '1') || 1,
        porPagina: POR_PAGINA,
    });
}

export function Documentos() {
    const carga = useLoaderData() as CargaDeDocumentos;
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const handle = useRotaAtual();
    const aviso = useAviso();

    /* O documento pode chegar aberto pela URL: é assim que a ficha do paciente
       abre um documento dele, em vez de avisar que "abre com a tela de Documentos". */
    const [aberto, setAberto] = useState<Documento | null>(() => {
        const id = params.get('documento');
        return id ? carga.itens.find((d) => d.id === id) ?? null : null;
    });
    const [modeloEmEdicao, setModeloEmEdicao] = useState<ModeloDeDocumento | 'novo' | null>(null);
    const [emitindo, setEmitindo] = useState(false);
    const [modeloEscolhido, setModeloEscolhido] = useState<ModeloDeDocumento | null>(null);
    const [filtrando, setFiltrando] = useState(false);

    const rota = rotaPorId('documentos');
    const aba = carga.aba;
    const subrota = handle?.subrota?.path ?? 'todos';
    /* O mesmo filtro do loader, sem a página: exportar levava só os dez da tela. */
    const filtroCompleto = () => ({
        aba,
        busca: params.get('q') ?? undefined,
        tipos: params.getAll('tipo'),
        status: params.getAll('status') as StatusDoDocumento[],
        favoritos: params.get('favoritos') === '1',
        pagina: 1,
        porPagina: TODOS_OS_REGISTROS,
    });
    const tiposMarcados = params.getAll('tipo');
    const statusMarcados = params.getAll('status');
    const soFavoritos = params.get('favoritos') === '1';

    const trocarParam = (chave: string, valor: string | null) => {
        setParams((atuais) => {
            const proximos = new URLSearchParams(atuais);
            if (!valor) proximos.delete(chave);
            else proximos.set(chave, valor);
            if (chave !== 'pagina') proximos.delete('pagina');
            return proximos;
        }, { replace: true });
    };

    const alternarLista = (chave: string, valor: string) => {
        setParams((atuais) => {
            const proximos = new URLSearchParams(atuais);
            const atual = proximos.getAll(chave);
            proximos.delete(chave);
            const novo = atual.includes(valor) ? atual.filter((v) => v !== valor) : [...atual, valor];
            for (const v of novo) proximos.append(chave, v);
            proximos.delete('pagina');
            return proximos;
        }, { replace: true });
    };

    const recarregar = () => revalidator.revalidate();

    const usarModelo = (modelo: ModeloDeDocumento) => {
        setModeloEscolhido(modelo);
        setEmitindo(true);
    };

    const favoritar = async (modelo: ModeloDeDocumento) => {
        try {
            await dados().salvarModeloDeDocumento({
                id: modelo.id,
                nome: modelo.nome,
                categoria: modelo.categoria,
                assinantes: modelo.assinantes,
                corpo: modelo.corpo,
                publicado: modelo.publicado,
                favorito: !modelo.favorito,
            });
            recarregar();
        } catch (e) {
            aviso({ title: 'Não foi possível favoritar', description: e instanceof Error ? e.message : '', tone: 'danger' });
        }
    };

    const acaoNoModelo = async (modelo: ModeloDeDocumento, acao: string) => {
        try {
            if (acao === 'duplicar') {
                const copia = await dados().duplicarModeloDeDocumento(modelo.id);
                aviso({ title: 'Modelo duplicado', description: `${copia.nome} · salvo como rascunho` });
            } else if (acao === 'publicar') {
                await dados().salvarModeloDeDocumento({
                    id: modelo.id, nome: modelo.nome, categoria: modelo.categoria,
                    assinantes: modelo.assinantes, corpo: modelo.corpo, publicado: true,
                });
                aviso({ title: 'Modelo publicado', description: modelo.nome });
            } else if (acao === 'excluir') {
                const { removido } = await dados().removerModeloDeDocumento(modelo.id);
                aviso(removido
                    ? { title: 'Modelo excluído', description: modelo.nome }
                    : {
                        title: 'Modelo despublicado, não excluído',
                        description: `${modelo.usos} documentos saíram dele e continuam apontando para ele`,
                        tone: 'warning',
                    });
            }
            recarregar();
        } catch (e) {
            aviso({ title: 'Não foi possível concluir', description: e instanceof Error ? e.message : '', tone: 'danger' });
        }
    };

    const colunasDeModelo: DataTableColumn<ModeloDeDocumento>[] = [
        {
            key: 'nome', header: 'Modelo', sortable: true,
            render: (m) => (
                <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <span data-parar-linha>
                        <IconButton
                            size="sm" icon={<Icon name={m.favorito ? 'star' : 'star-off'} size={15} />}
                            label={m.favorito ? `Remover ${m.nome} dos favoritos` : `Favoritar ${m.nome}`}
                            onClick={() => void favoritar(m)}
                        />
                    </span>
                    <span>
                        <span style={{ display: 'block', fontWeight: 600 }}>{m.nome}</span>
                        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)' }}>
                            {m.campos} campos dinâmicos · {m.assinantes}
                        </span>
                    </span>
                </span>
            ),
        },
        { key: 'categoria', header: 'Categoria', render: (m) => <Badge tone={CATEGORIA_TOM[m.categoria] ?? 'neutral'}>{m.categoria}</Badge> },
        { key: 'usos', header: 'Usos', align: 'right' },
        { key: 'atualizadoEm', header: 'Atualizado', sortable: true, render: (m) => dataEHoraPorExtenso(m.atualizadoEm) },
        { key: 'publicado', header: 'Status', render: (m) => <Badge tone={m.publicado ? 'success' : 'neutral'} dot>{m.publicado ? 'Publicado' : 'Rascunho'}</Badge> },
        {
            key: 'acoes', header: '', align: 'right', width: 120,
            render: (m) => (
                <span data-parar-linha style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <Button size="sm" variant="secondary" disabled={!m.publicado} onClick={() => usarModelo(m)}>Usar</Button>
                    <Menu
                        align="right" width={220}
                        onSelect={(id) => (id === 'editar' ? setModeloEmEdicao(m) : void acaoNoModelo(m, id))}
                        trigger={<Button size="sm" variant="ghost" iconLeft={<Icon name="ellipsis" size={15} />} aria-label={`Mais ações — ${m.nome}`} />}
                        items={[
                            { id: 'editar', label: 'Editar modelo', icon: 'pencil' },
                            { id: 'duplicar', label: 'Duplicar', icon: 'copy' },
                            { id: 'publicar', label: 'Publicar', icon: 'check', disabled: m.publicado },
                            { divider: true },
                            { id: 'excluir', label: m.usos > 0 ? 'Tirar de circulação' : 'Excluir modelo', icon: 'trash-2', tone: 'danger' },
                        ]}
                    />
                </span>
            ),
        },
    ];

    const filtrosAplicados = [
        ...tiposMarcados.map((t) => ({ id: `tipo:${t}`, label: `Tipo: ${t}` })),
        ...statusMarcados.map((s) => ({ id: `status:${s}`, label: `Status: ${STATUS[s as StatusDoDocumento].rotulo}` })),
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Documentos"
                description="Termos, contratos e formulários — com o texto que foi assinado guardado no documento"
                actions={
                    aba === 'modelos' ? (
                        <>
                            <ExportMenu<ModeloDeDocumento>
                                label="Exportar" size="md"
                                carregarTudo={async () => (await dados().documentos(filtroCompleto())).modelos}
                                payload={{
                                    title: 'Modelos de documentos', subtitle: `${carga.total} modelos`, rows: carga.modelos,
                                    columns: [
                                        { header: 'Modelo', value: (m) => m.nome },
                                        { header: 'Categoria', value: (m) => m.categoria },
                                        { header: 'Assinantes', value: (m) => m.assinantes },
                                        { header: 'Campos', value: (m) => String(m.campos) },
                                        { header: 'Usos', value: (m) => String(m.usos) },
                                        { header: 'Status', value: (m) => (m.publicado ? 'Publicado' : 'Rascunho') },
                                    ],
                                }}
                            />
                            <Button iconLeft={<Icon name="plus" size={15} />} onClick={() => setModeloEmEdicao('novo')}>Novo modelo</Button>
                        </>
                    ) : (
                        <>
                            <ExportMenu<Documento>
                                label="Exportar" size="md"
                                carregarTudo={async () => (await dados().documentos(filtroCompleto())).itens}
                                payload={{
                                    title: 'Documentos', subtitle: `${carga.total} documentos`, rows: carga.itens,
                                    columns: [
                                        { header: 'Documento', value: (d) => d.titulo },
                                        { header: 'Paciente', value: (d) => d.paciente },
                                        { header: 'Tipo', value: (d) => d.tipo },
                                        { header: 'Situação', value: (d) => STATUS[d.status].rotulo },
                                        { header: 'Atualizado', value: (d) => dataEHoraPorExtenso(d.atualizadoEm) },
                                        { header: 'Páginas', value: (d) => String(d.paginas) },
                                    ],
                                }}
                            />
                            <Button
                                iconLeft={<Icon name="plus" size={15} />}
                                onClick={() => { setModeloEscolhido(null); setEmitindo(true); }}
                            >
                                Novo documento
                            </Button>
                        </>
                    )
                }
            >
                <Tabs
                    ariaLabel="Seções de documentos" activeId={subrota}
                    onChange={(id) => navigate(`/app/documentos/${id}`)}
                    tabs={(rota?.children ?? []).map((c) => ({
                        id: c.path,
                        label: c.label,
                        count: c.path === 'todos' ? carga.totais.todos
                            : c.path === 'assinaturas' ? carga.totais.aguardando
                                : carga.totais.modelos,
                    }))}
                />
            </PageHeader>

            <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                {carga.indicadores.map((i) => (
                    <KpiCard key={i.id} label={i.rotulo} value={i.valor} hint={i.apoio} deltaTone={i.tendencia} icon={<Icon name={i.icone} size={16} />} />
                ))}
            </div>

            {/* O aviso conta há quanto tempo, e leva à fila: "3 pendentes" sem
                caminho é informação que não vira ação. */}
            {aba !== 'modelos' && carga.parados.length > 0 && (
                <Alert
                    tone="warning" dismissible={false}
                    title={`${carga.parados.length} ${carga.parados.length === 1 ? 'documento aguardando assinatura' : 'documentos aguardando assinatura'} há mais de 48h`}
                    action={aba === 'todos' ? (
                        <Button size="sm" variant="secondary" onClick={() => navigate('/app/documentos/assinaturas')}>Ver a fila</Button>
                    ) : undefined}
                >
                    {carga.parados.map((d) => `${d.titulo} · ${d.paciente}`).join(' · ')}
                </Alert>
            )}

            <FilterBar
                search={(
                    <SearchField
                        value={params.get('q') ?? ''} onChange={(v) => trocarParam('q', v || null)} width={260}
                        placeholder={aba === 'modelos' ? 'Buscar modelo' : 'Buscar documento ou paciente'}
                    />
                )}
                filters={aba === 'modelos' ? (soFavoritos ? [{ id: 'fav', label: 'Apenas favoritos' }] : []) : filtrosAplicados}
                onRemoveFilter={(id) => {
                    if (id === 'fav') trocarParam('favoritos', null);
                    else {
                        const [grupo, valor] = String(id).split(':');
                        alternarLista(grupo === 'tipo' ? 'tipo' : 'status', valor ?? '');
                    }
                }}
                onAddFilter={() => (aba === 'modelos' ? trocarParam('favoritos', '1') : setFiltrando(true))}
                resultLabel={`${carga.total} ${aba === 'modelos' ? 'modelos' : 'documentos'}`}
            />

            {aba === 'modelos' ? (
                carga.modelos.length === 0 ? (
                    <Card padding={0}>
                        <EmptyState
                            compact icon="file-plus" title="Nenhum modelo encontrado"
                            description="Ajuste a busca ou crie um novo modelo."
                            action={<Button size="sm" onClick={() => setModeloEmEdicao('novo')}>Novo modelo</Button>}
                        />
                    </Card>
                ) : (
                    <DataTable
                        columns={colunasDeModelo} rows={carga.modelos} rowKey="id"
                        onRowClick={setModeloEmEdicao} rowLabel={(m) => m.nome}
                    />
                )
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'var(--layout-colunas, minmax(0,1.6fr) minmax(0,1fr))', gap: 16, alignItems: 'start' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {carga.itens.length === 0 && (
                            <Card padding={0}>
                                <EmptyState
                                    compact icon="search-x" title="Nenhum documento encontrado"
                                    description="Ajuste a busca ou remova os filtros aplicados."
                                />
                            </Card>
                        )}
                        {carga.itens.map((d) => (
                            <DocumentCard
                                key={d.id} title={d.titulo} patient={d.paciente} pages={d.paginas}
                                status={d.status} updatedAt={dataEHoraPorExtenso(d.atualizadoEm)}
                                onClick={() => setAberto(d)}
                            />
                        ))}
                    </div>

                    <Card
                        title="Modelos favoritos" subtitle="Emissão em um clique" padding={18}
                        actions={<Button size="sm" variant="ghost" onClick={() => navigate('/app/documentos/modelos')}>Ver todos</Button>}
                    >
                        {carga.modelos.filter((m) => m.favorito).length === 0 ? (
                            <EmptyState
                                compact icon="file-plus" title="Nenhum modelo favoritado"
                                description="Marque um modelo como favorito para emiti-lo em um clique."
                            />
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {carga.modelos.filter((m) => m.favorito).map((m) => (
                                    <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
                                        <Icon name="file-text" size={15} />
                                        <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.nome}</span>
                                        <Button size="sm" variant="secondary" disabled={!m.publicado} onClick={() => usarModelo(m)}>Usar</Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>
                </div>
            )}

            <Pagination
                page={carga.pagina} pageCount={carga.paginas} total={carga.total} pageSize={POR_PAGINA}
                onChange={(p) => trocarParam('pagina', String(p))}
            />

            {/* Dentro de um grupo vale qualquer opção marcada; entre grupos,
                todas precisam bater — é o que a descrição diz, e é o que o
                loader faz. */}
            <Modal
                open={filtrando} onClose={() => setFiltrando(false)} width={480}
                title="Filtrar documentos"
                description="Dentro de um grupo vale qualquer opção marcada; entre grupos, todas precisam bater."
                footer={
                    <>
                        {filtrosAplicados.length > 0 && (
                            <Button
                                variant="ghost"
                                onClick={() => setParams((atuais) => {
                                    const proximos = new URLSearchParams(atuais);
                                    proximos.delete('tipo');
                                    proximos.delete('status');
                                    return proximos;
                                }, { replace: true })}
                            >
                                Limpar tudo
                            </Button>
                        )}
                        <span style={{ marginLeft: 'auto' }} />
                        <Button onClick={() => setFiltrando(false)}>
                            Ver {carga.total} {carga.total === 1 ? 'documento' : 'documentos'}
                        </Button>
                    </>
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                    <div>
                        <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
                            Tipo de documento
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            {carga.tipos.map((t) => (
                                <Checkbox key={t} label={t} checked={tiposMarcados.includes(t)} onChange={() => alternarLista('tipo', t)} />
                            ))}
                        </div>
                    </div>
                    <div style={{ paddingTop: 14, borderTop: '1px solid var(--border-subtle)' }}>
                        <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
                            Situação
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            {STATUS_FILTRAVEIS.map((s) => (
                                <Checkbox key={s} label={STATUS[s].rotulo} checked={statusMarcados.includes(s)} onChange={() => alternarLista('status', s)} />
                            ))}
                        </div>
                    </div>
                </div>
            </Modal>

            <DetalheDoDocumento documento={aberto} onClose={() => setAberto(null)} onDecidido={recarregar} />

            <ModeloModal alvo={modeloEmEdicao} onClose={() => setModeloEmEdicao(null)} onSalvo={recarregar} />

            <NovoDocumentoModal
                aberto={emitindo} modeloInicial={modeloEscolhido} modelos={carga.modelos}
                pacientes={carga.pacientes} procedimentos={carga.procedimentos} profissionais={carga.profissionais}
                onClose={() => { setEmitindo(false); setModeloEscolhido(null); }}
                onEmitido={recarregar}
            />
        </div>
    );
}
